/**
 * controlScheme - resolves which control scheme drives the session.
 *
 *  - 'touch'   : forced on mobile devices (MobileUtils.isMobile()); the
 *                thumbstick + drag-look + tap controls (touchControls.js).
 *  - 'classic' : WASD + pointer-lock mouse look + center-crosshair picking
 *                (the historical behavior; the desktop default).
 *  - 'hybrid'  : WASD + drag-to-look (no pointer lock) + direct cursor clicks
 *                on exhibits. Keyboard-only movement - no click-to-walk.
 *  - 'easy'    : click-to-walk + drag-to-look (no pointer lock) + direct
 *                cursor clicks on exhibits.
 *
 * Consumers should ask about CAPABILITY (usesCursorPicking / usesClickToWalk)
 * rather than comparing scheme ids, so adding a scheme stays a local change.
 *
 * The desktop choice persists in settingsStore under 'controlScheme' (picked
 * at onboarding, switchable in the settings menu). Touch is never persisted -
 * it's a device property, not a preference.
 */

import { MobileUtils } from '../utils/mobileUtils.js';
import { getSetting, setSetting, subscribe } from '../utils/settingsStore.js';

/** @typedef {'classic'|'easy'|'hybrid'|'touch'} ControlScheme */

/** The schemes a desktop user may choose between (touch is device-resolved). */
const DESKTOP_SCHEMES = ['classic', 'hybrid', 'easy'];

/** @returns {boolean} guarded MobileUtils.isMobile() (safe without a DOM) */
export function isTouchDevice() {
  try {
    return MobileUtils.isMobile();
  } catch {
    return false;
  }
}

/** @returns {ControlScheme} the scheme that should drive input right now */
export function getScheme() {
  if (isTouchDevice()) return 'touch';
  const stored = String(getSetting('controlScheme'));
  return DESKTOP_SCHEMES.includes(stored)
    ? /** @type {ControlScheme} */ (stored)
    : /** @type {ControlScheme} */ ('classic');
}

/**
 * Persist a desktop scheme choice ('classic' | 'hybrid' | 'easy'). Ignores
 * anything else ('touch' is resolved from the device, never stored).
 * @param {string} scheme
 */
export function setScheme(scheme) {
  if (!DESKTOP_SCHEMES.includes(scheme)) return;
  setSetting('controlScheme', scheme);
}

/**
 * Does this scheme resolve interactions at the CURSOR (rather than a
 * center crosshair or not at all)? True for the two free-cursor desktop
 * schemes, which also drag-to-look and so want grab/grabbing feedback.
 * @param {ControlScheme|string} [scheme] defaults to the live scheme
 * @returns {boolean}
 */
export function usesCursorPicking(scheme = getScheme()) {
  return scheme === 'easy' || scheme === 'hybrid';
}

/**
 * Does this scheme walk to an unhandled tap on `metadata.oggiWalkable` floor?
 * Deliberately false for 'hybrid': its movement is keyboard-only.
 * @param {ControlScheme|string} [scheme] defaults to the live scheme
 * @returns {boolean}
 */
export function usesClickToWalk(scheme = getScheme()) {
  return scheme === 'easy' || scheme === 'touch';
}

/**
 * Subscribe to scheme changes. Fires with the resolved scheme (so a touch
 * device never observes a switch away from 'touch').
 * @param {(scheme: ControlScheme) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onSchemeChange(fn) {
  return subscribe((key) => {
    if (key === 'controlScheme') fn(getScheme());
  });
}
